import { useState, useCallback } from 'react';
import { useLongPolling } from '../../hooks/useLongPolling';
import { useDiscord } from '../../context/DiscordContext';
import { WSMessage, LobbyParticipant } from '../../types/game';
import './LobbyScreen.css';

interface LobbyScreenProps {
  roomId: string;
  onGameStart: () => void;
  onPlayBot: () => void;
}

export function LobbyScreen({ roomId, onGameStart, onPlayBot }: LobbyScreenProps) {
  const { oderId } = useDiscord();
  const [participants, setParticipants] = useState<LobbyParticipant[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [myRole, setMyRole] = useState<'player1' | 'player2' | 'spectator' | null>(null);
  const [isHost, setIsHost] = useState(false);

  const handleMessage = useCallback((message: WSMessage) => {
    switch (message.type) {
      case 'lobby_state': {
        const { participants: lobbyParticipants } = message.payload;
        if (lobbyParticipants) {
          setParticipants(lobbyParticipants);
          // Update my state from lobby state
          const me = lobbyParticipants.find((p: LobbyParticipant) => p.oderId === oderId);
          if (me) {
            setMyRole(me.assignedRole);
            setIsHost(me.isHost);
            setIsReady(me.ready);
          }
        }
        break;
      }

      case 'lobby_participant_joined': {
        const { oderId: joinedId, ready, assignedRole, isHost: joinedIsHost } = message.payload;
        setParticipants(prev => {
          if (prev.some(p => p.oderId === joinedId)) return prev;
          return [...prev, { oderId: joinedId, ready, assignedRole, isHost: joinedIsHost }];
        });
        break;
      }

      case 'lobby_participant_ready': {
        const { oderId: readyId, ready, assignedRole } = message.payload;
        setParticipants(prev => prev.map(p =>
          p.oderId === readyId ? { ...p, ready, assignedRole } : p
        ));
        // Update my role if it's me
        if (readyId === oderId) {
          setMyRole(assignedRole);
        }
        break;
      }

      case 'lobby_participant_left': {
        const { oderId: leftId } = message.payload;
        setParticipants(prev => prev.filter(p => p.oderId !== leftId));
        break;
      }

      case 'role_assigned': {
        const { oderId: assignedId, role, isHost: assignedIsHost } = message.payload;
        if (assignedId === oderId) {
          setMyRole(role);
          setIsHost(assignedIsHost);
        }
        break;
      }

      case 'your_player_id': {
        const { playerId, isHost: playerIsHost } = message.payload;
        setMyRole(playerId === 1 ? 'player1' : 'player2');
        setIsHost(playerIsHost);
        break;
      }

      case 'your_spectator_id': {
        setMyRole('spectator');
        break;
      }

      case 'game_start': {
        onGameStart();
        break;
      }
    }
  }, [oderId, onGameStart]);

  const { isConnected, sendMessage } = useLongPolling({
    roomId,
    oderId,
    enabled: true,
    onMessage: handleMessage,
  });

  const handleToggleReady = useCallback(() => {
    const newReady = !isReady;
    setIsReady(newReady);
    sendMessage({
      type: 'lobby_ready',
      payload: { ready: newReady }
    });
  }, [isReady, sendMessage]);

  // Count ready players (those with player1 or player2 role)
  const assignedPlayers = participants.filter(p =>
    p.assignedRole === 'player1' || p.assignedRole === 'player2'
  );
  const readyCount = assignedPlayers.filter(p => p.ready).length;

  // Get role display text
  const getRoleText = (role: string | null) => {
    switch (role) {
      case 'player1': return 'Player 1';
      case 'player2': return 'Player 2';
      case 'spectator': return 'Spectator';
      default: return 'Waiting...';
    }
  };

  return (
    <div className="lobby-screen">
      <header className="lobby-header">
        <h1 className="gold-gradient">Alchemy Duel</h1>
        <p className="lobby-subtitle">Discord Activity</p>
      </header>

      <div className="lobby-content">
        <div className="lobby-players">
          <h2>Lobby ({participants.length} connected)</h2>
          <div className="player-list">
            {participants.length === 0 && (
              <p className="waiting-text">Waiting for players...</p>
            )}
            {participants.map((participant) => (
              <div
                key={participant.oderId}
                className={`player-item ${participant.ready ? 'ready' : ''} ${participant.oderId === oderId ? 'me' : ''}`}
              >
                <div className="player-info">
                  <span className="player-name">
                    {participant.oderId === oderId ? 'You' : `User ${participant.oderId.slice(0, 8)}...`}
                    {participant.isHost && <span className="host-badge">HOST</span>}
                  </span>
                  <span className={`role-badge ${participant.assignedRole || 'pending'}`}>
                    {getRoleText(participant.assignedRole)}
                  </span>
                </div>
                <span className={`ready-status ${participant.ready ? 'ready' : 'not-ready'}`}>
                  {participant.ready ? 'READY' : 'Not Ready'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="lobby-actions">
          {myRole === 'spectator' ? (
            <div className="spectator-notice">
              <p>You are a spectator</p>
              <p className="hint">Waiting for game to start...</p>
            </div>
          ) : (
            <>
              <button
                className={`ready-btn ${isReady ? 'ready' : ''}`}
                onClick={handleToggleReady}
                disabled={!isConnected}
              >
                {isReady ? 'Cancel Ready' : 'Ready!'}
              </button>
              {myRole && (
                <p className="my-role">
                  You are {getRoleText(myRole)}
                  {isHost && ' (Host)'}
                </p>
              )}
              {!myRole && isReady && (
                <p className="waiting-role">Waiting for role assignment...</p>
              )}
            </>
          )}

          <div className="lobby-divider">or</div>

          <button
            className="bot-btn"
            onClick={onPlayBot}
          >
            Play vs Bot
          </button>
        </div>

        {readyCount > 0 && (
          <p className="ready-count">{readyCount}/2 players ready</p>
        )}

        {!isConnected && (
          <p className="connection-status">Connecting...</p>
        )}
      </div>
    </div>
  );
}
